'use client'

import { useTranslations } from 'next-intl'
import Link from 'next/link'
import { type ReactNode, useEffect, useMemo, useRef, useState } from 'react'

import IconClose from '@/components/assets/icon_close.svg'
import IconCode from '@/components/assets/icon_code.svg'
import IconCopy from '@/components/assets/icon_copy.svg'
import IconDiscord from '@/components/assets/icon_discord.svg'
import IconFB from '@/components/assets/icon_facebook.svg'
import IconX from '@/components/assets/icon_X.svg'
import OptimizedImage from '@/components/OptimizedImage'
import ViverseButton from '@/components/ViverseButton'
import ViverseCheckbox from '@/components/ViverseCheckbox'
import ViverseInput from '@/components/ViverseInput'
import ViverseTextarea from '@/components/ViverseTextarea'
import { IFRAME_SEARCH_PARAM_KEYS } from '@/constants/iframe'
import { useUAContext } from '@/context/UAContext'
import { useShareLink } from '@/hooks/useShareLink'
import { createEmbedCode } from '@/lib/embed'
import { showToast } from '@/lib/toast'
import { cn } from '@/lib/utils'
import type { WorldCardInfo } from '@/types/viverse-world-card'

type SharePanelProps = {
  onCloseAction: () => void
  shareUrl: string
  title: string
  info: WorldCardInfo
  currentTime?: number
  positionFixed?: boolean
  isVideoWorld?: boolean
  is3DCommerce?: boolean
  isStandalone?: boolean
  initialView?: 'share' | 'embed'
  previewImageAlt?: string
  previewImageSrc?: string
  renderContentOnly?: boolean
  useControlBarDialog?: boolean
  useControlBarEmbedDialog?: boolean
  onSwitchToEmbedAction?: () => void
}

type ShareActionLinkProps = {
  href: string
  label: string
  onSelect: () => void
  children: ReactNode
  iconContainerClassName?: string
}

const BASE_EMBED_QUERY_PARAMS = [
  IFRAME_SEARCH_PARAM_KEYS.FULL3D,
  IFRAME_SEARCH_PARAM_KEYS.NO_COOKIE,
]

const EMBED_QUERY_PARAMS_WITH_LOADING = [IFRAME_SEARCH_PARAM_KEYS.IS_3D_COMMERCE]
const EMBED_QUERY_PARAMS_WITH_VIEW = [IFRAME_SEARCH_PARAM_KEYS.VIEW]
const EMBED_QUERY_PARAMS_WITH_CONTROL_BAR = [IFRAME_SEARCH_PARAM_KEYS.CONTROL_BAR]

function getEmbedQueryParams(options: {
  controlBarEnabled?: boolean
  customLoadingEnabled: boolean
  is3DCommerce?: boolean
}) {
  const { controlBarEnabled = false, customLoadingEnabled = false, is3DCommerce = false } = options
  return [
    ...BASE_EMBED_QUERY_PARAMS,
    ...(customLoadingEnabled ? EMBED_QUERY_PARAMS_WITH_LOADING : []),
    ...(is3DCommerce ? EMBED_QUERY_PARAMS_WITH_VIEW : []),
    ...(controlBarEnabled ? EMBED_QUERY_PARAMS_WITH_CONTROL_BAR : []),
  ]
}

function secondsToMMSS(totalSeconds: number): string {
  const s = Math.max(0, Math.floor(totalSeconds))
  const mm = Math.floor(s / 60)
  const ss = s % 60
  return `${String(mm).padStart(2, '0')}:${String(ss).padStart(2, '0')}`
}

function mmssToSeconds(value: string): number {
  const parts = value.split(':')
  if (parts.length !== 2) return 0
  const mm = parseInt(parts[0] ?? '0', 10)
  const ss = parseInt(parts[1] ?? '0', 10)
  if (isNaN(mm) || isNaN(ss)) return 0
  return mm * 60 + ss
}

function normalizeTimeInput(value: string): string {
  const trimmed = value.trim()
  // Plain integer → treat as total seconds
  if (/^\d+$/.test(trimmed)) {
    return secondsToMMSS(parseInt(trimmed, 10))
  }
  // M:SS or MM:SS
  const match = trimmed.match(/^(\d{1,2}):(\d{1,2})$/)
  if (match) {
    const mm = parseInt(match[1]!, 10)
    const ss = parseInt(match[2]!, 10)
    if (ss < 60) return secondsToMMSS(mm * 60 + ss)
  }
  return '00:00'
}

function ShareActionLink({
  href,
  label,
  onSelect,
  children,
  iconContainerClassName,
}: ShareActionLinkProps) {
  return (
    <Link
      href={href}
      target="_blank"
      rel="noopener noreferrer"
      className="flex min-w-22 flex-col items-center gap-2.5 text-center"
      onClick={onSelect}
    >
      <span
        className={cn(
          'flex size-12 items-center justify-center overflow-hidden rounded-full transition-colors hover:brightness-110',
          iconContainerClassName,
        )}
      >
        {children}
      </span>
      <span className="caption text-vive-primary-20">{label}</span>
    </Link>
  )
}

function ShareView({
  onCloseAction,
  shareUrl,
  title,
  onSwitchToEmbed,
  currentTime,
  isVideoWorld,
  isStandalone,
  previewImageAlt,
  previewImageSrc,
  useControlBarDialog,
}: {
  onCloseAction: () => void
  shareUrl: string
  title: string
  onSwitchToEmbed: () => void
  currentTime?: number
  isVideoWorld?: boolean
  isStandalone?: boolean
  previewImageAlt?: string
  previewImageSrc?: string
  useControlBarDialog?: boolean
}) {
  const t = useTranslations()
  const { isMobile } = useUAContext()

  const [startAtEnabled, setStartAtEnabled] = useState(false)
  const [startAtValue, setStartAtValue] = useState(() => secondsToMMSS(currentTime ?? 0))
  const startAtInputRef = useRef<HTMLInputElement>(null)
  const [autoplayEnabled, setAutoplayEnabled] = useState(false)
  const [hideControlsEnabled, setHideControlsEnabled] = useState(false)
  const [clipboardAllowed, setClipboardAllowed] = useState(false)

  useEffect(() => {
    let mounted = true
    if (typeof navigator !== 'undefined' && navigator.permissions?.query) {
      navigator.permissions
        .query({ name: 'clipboard-write' as PermissionName })
        .then((status) => {
          if (mounted && status.state !== 'denied') setClipboardAllowed(true)
        })
        .catch((error) => {
          // eslint-disable-next-line no-console
          console.warn(
            '[SharePanel] clipboard-write permission query not supported; showing copy button optimistically',
            error,
          )
          if (mounted) setClipboardAllowed(true)
        })
    } else {
      setClipboardAllowed(true)
    }
    return () => {
      mounted = false
    }
  }, [])

  useEffect(() => {
    if (startAtEnabled) {
      startAtInputRef.current?.focus()
    }
  }, [startAtEnabled])

  const effectiveUrl = useMemo(() => {
    try {
      const url = new URL(shareUrl)
      if (startAtEnabled) {
        const seconds = mmssToSeconds(startAtValue)
        if (seconds > 0) url.searchParams.set('t', String(seconds))
      }
      if (isVideoWorld) {
        url.searchParams.set('autoplay', autoplayEnabled ? '1' : '0')
        if (hideControlsEnabled) url.searchParams.set('controls', '0')
      }
      return url.toString()
    } catch {
      return shareUrl
    }
  }, [shareUrl, startAtEnabled, startAtValue, autoplayEnabled, hideControlsEnabled, isVideoWorld])

  const { createSharePlatformLink } = useShareLink(shareUrl)

  const handleDiscordShare = async () => {
    const url = new URL(effectiveUrl)
    url.searchParams.set(IFRAME_SEARCH_PARAM_KEYS.FORCE_LANDSCAPE_IOS, '1')

    const shareUrl = url.toString()

    if (isMobile && typeof navigator.share === 'function') {
      try {
        await navigator.share({ url: shareUrl, title })
        onCloseAction()
      } catch (err) {
        if (err instanceof Error && err.name === 'AbortError') return
        showToast({ message: t('share-panel.copy-failed'), type: 'error' })
      }
      return
    }

    await handleCopyLink(shareUrl)
  }

  const handleCopyLink = async (url: string) => {
    try {
      await navigator.clipboard.writeText(url)
      showToast({
        message: t('profile-page.share-link-copied'),
        type: 'success',
      })
      onCloseAction()
    } catch {
      showToast({
        message: t('share-panel.copy-failed'),
        type: 'error',
      })
    }
  }

  const compactShareActions = (
    <div className="flex shrink-0 items-center justify-center gap-2">
      <button
        type="button"
        className="@max-embed-lg/embed:size-8 flex size-12 items-center justify-center rounded-full bg-black/25"
        aria-label={t('embed-modal.title')}
        onClick={onSwitchToEmbed}
      >
        <IconCode className="@max-embed-lg/embed:size-3 size-5 fill-white" aria-hidden="true" />
      </button>
      <a
        className="@max-embed-lg/embed:size-8 flex size-12 items-center justify-center rounded-full bg-black/25"
        href={createSharePlatformLink('facebook')}
        target="_blank"
        rel="noopener noreferrer"
        aria-label={t('profile-page.share-facebook')}
        onClick={onCloseAction}
      >
        <IconFB className="@max-embed-lg/embed:h-4 h-6 w-auto" aria-hidden="true" />
      </a>
      <a
        className="@max-embed-lg/embed:size-8 flex size-12 items-center justify-center rounded-full bg-black/25"
        href={createSharePlatformLink('twitter', title)}
        target="_blank"
        rel="noopener noreferrer"
        aria-label={t('profile-page.share-twitter')}
        onClick={onCloseAction}
      >
        <IconX className="@max-embed-lg/embed:size-4 size-6" aria-hidden="true" />
      </a>
      <button
        type="button"
        className="@max-embed-lg/embed:size-8 flex size-12 items-center justify-center rounded-full bg-black/25"
        aria-label={t('profile-page.share-discord')}
        onClick={handleDiscordShare}
      >
        <IconDiscord className="@max-embed-lg/embed:size-4 size-6" aria-hidden="true" />
      </button>
    </div>
  )

  if (useControlBarDialog) {
    return (
      <div className="@max-embed-lg/embed:gap-3.5 flex w-full flex-col items-center gap-4">
        {previewImageSrc && (
          <div className="@max-embed-lg/embed:h-16 @max-embed-lg/embed:w-[114px] @max-embed-md/embed:hidden relative h-25 w-[177px] overflow-hidden rounded-2xl">
            <OptimizedImage
              src={previewImageSrc}
              alt={previewImageAlt ?? title}
              fill
              sizes="177px"
              className="object-cover"
            />
          </div>
        )}
        <h2 className="@max-embed-lg/embed:text-sm @max-embed-lg/embed:leading-[22px] @max-embed-lg/embed:tracking-[0.15px] text-center text-base leading-6 font-semibold tracking-[0.2px] text-white">
          {t('share-modal.title')}
        </h2>
        {compactShareActions}
        <div className="flex w-full items-center gap-2.5">
          <input
            readOnly
            value={effectiveUrl}
            spellCheck={false}
            autoCapitalize="off"
            autoCorrect="off"
            className="body-2 bg-vive-primary-50/60 @max-embed-lg/embed:h-7 @max-embed-lg/embed:text-xs @max-embed-lg/embed:leading-4 h-[34px] min-w-0 flex-1 cursor-text rounded-[3px] border-none px-4 py-1.5 text-white outline-none"
          />
          {clipboardAllowed && (
            <ViverseButton
              variant="custom"
              size="custom"
              className="@max-embed-lg/embed:h-7 flex h-[34px] w-14 shrink-0 items-center justify-center rounded-full bg-white/15 p-0 hover:bg-white/20 active:bg-white/15"
              aria-label={t('share-panel.copy-button')}
              onClick={() => handleCopyLink(effectiveUrl)}
            >
              <IconCopy className="size-5 shrink-0 fill-white" aria-hidden="true" />
            </ViverseButton>
          )}
        </div>
      </div>
    )
  }

  return (
    <>
      <div className="flex items-center">
        <p className="subheader-1 text-vive-white w-full text-center">{t('action-list.share')}</p>
      </div>

      <div className="flex flex-col py-2">
        <div className="flex items-start justify-center">
          <button
            type="button"
            className="flex min-w-22 flex-col items-center gap-2.5 text-center"
            onClick={onSwitchToEmbed}
          >
            <span className="bg-vive-primary-60 hover:bg-vive-primary-50 flex size-12 items-center justify-center overflow-hidden rounded-full transition-colors">
              <IconCode className="size-6 fill-white" />
            </span>
            <span className="caption text-vive-primary-20">{t('embed-modal.title')}</span>
          </button>

          {isStandalone && !isVideoWorld && (
            <button
              type="button"
              className="flex min-w-22 flex-col items-center gap-2.5 text-center"
              onClick={handleDiscordShare}
            >
              <span className="flex size-12 items-center justify-center overflow-hidden rounded-full bg-[#5865F2] transition-colors hover:brightness-110">
                <IconDiscord className="size-6" />
              </span>
              <span className="caption text-vive-primary-20">
                {t('profile-page.share-discord')}
              </span>
            </button>
          )}

          <ShareActionLink
            href={createSharePlatformLink('facebook')}
            label={t('profile-page.share-facebook')}
            onSelect={onCloseAction}
            iconContainerClassName="bg-[#385289]"
          >
            <IconFB className="h-6 w-auto" />
          </ShareActionLink>

          <ShareActionLink
            href={createSharePlatformLink('twitter', title)}
            label={t('profile-page.share-twitter')}
            onSelect={onCloseAction}
            iconContainerClassName="bg-black/90 hover:bg-black/60"
          >
            <IconX className="size-6" />
          </ShareActionLink>
        </div>
        <div className="my-3 h-[1.5px] w-full bg-[#F1F7FE]/20" />
        <div className="flex items-center gap-2.5 py-2">
          <input
            readOnly
            value={effectiveUrl}
            spellCheck={false}
            autoCapitalize="off"
            autoCorrect="off"
            className="body-2 text-vive-white bg-vive-primary-50/60 h-8.5 min-w-0 flex-1 cursor-text rounded-[3px] border-none px-4 py-1.5 outline-none"
          />
          {clipboardAllowed && (
            <ViverseButton
              size="custom"
              className="h-8.5 shrink-0 rounded-full px-3"
              onClick={() => handleCopyLink(effectiveUrl)}
            >
              <IconCopy className="mr-1.5 size-4 fill-white" />
              <span className="text-sm">{t('share-panel.copy-button')}</span>
            </ViverseButton>
          )}
        </div>
        {isVideoWorld && (
          <>
            <div className="my-3 h-[1.5px] w-full bg-[#F1F7FE]/20" />

            <div className="flex flex-col pb-2">
              {/* Start at */}
              <div className="flex items-center justify-between">
                <ViverseCheckbox
                  id="share-start-at"
                  checked={startAtEnabled}
                  onCheckedChange={(v) => setStartAtEnabled(!!v)}
                  containerClassName="py-1.5"
                >
                  <span className="caption text-vive-primary-20">
                    {t('share-modal.start-time-checkbox-label')}
                  </span>
                </ViverseCheckbox>
                <div className="w-20 shrink-0">
                  <ViverseInput
                    ref={startAtInputRef}
                    value={startAtValue}
                    disabled={!startAtEnabled}
                    onChange={(e) =>
                      setStartAtValue(e.target.value.replace(/[^\d:]/g, '').slice(0, 5))
                    }
                    onBlur={() => setStartAtValue(normalizeTimeInput(startAtValue))}
                    placeholder="00:00"
                    className="h-7 min-h-0 text-center text-sm"
                    containerClassName={cn(
                      'border-transparent',
                      !startAtEnabled && 'focus-within:border-transparent hover:border-transparent',
                    )}
                  />
                </div>
              </div>

              {/* Autoplay */}
              <ViverseCheckbox
                id="share-autoplay"
                checked={autoplayEnabled}
                onCheckedChange={(v) => setAutoplayEnabled(!!v)}
                containerClassName="py-1.5"
              >
                <span className="caption text-vive-primary-20">
                  {t('share-modal.auto-play-checkbox-label')}
                </span>
              </ViverseCheckbox>

              {/* Hide controls */}
              <ViverseCheckbox
                id="share-hide-controls"
                checked={hideControlsEnabled}
                onCheckedChange={(v) => setHideControlsEnabled(!!v)}
                containerClassName="py-1.5"
              >
                <span className="caption text-vive-primary-20">
                  {t('share-modal.hide-controls-checkbox-label')}
                </span>
              </ViverseCheckbox>
            </div>
          </>
        )}
      </div>
    </>
  )
}

function EmbedView({
  info,
  onCloseAction,
  is3DCommerce,
  isStandalone,
  isVideoWorld,
  currentTime,
  useControlBarEmbedDialog,
}: {
  info: WorldCardInfo
  onCloseAction: () => void
  is3DCommerce?: boolean
  isStandalone?: boolean
  isVideoWorld?: boolean
  currentTime?: number
  useControlBarEmbedDialog?: boolean
}) {
  const t = useTranslations()
  const textInputRef = useRef<HTMLInputElement>(null)
  const startAtInputRef = useRef<HTMLInputElement>(null)
  const embedCodeTextareaRef = useRef<HTMLTextAreaElement>(null)

  const [customLoadingEnabled, setCustomLoadingEnabled] = useState(false)
  const [startAtEnabled, setStartAtEnabled] = useState(false)
  const [startAtValue, setStartAtValue] = useState(() => secondsToMMSS(currentTime ?? 0))
  const [autoplayEnabled, setAutoplayEnabled] = useState(false)
  const [hideControlsEnabled, setHideControlsEnabled] = useState(false)
  const [includeControlBar, setIncludeControlBar] = useState(false)

  const [textAreaInputValue, setTextAreaInputValue] = useState(() =>
    createEmbedCode(info, getEmbedQueryParams({ customLoadingEnabled: false, is3DCommerce })),
  )

  useEffect(() => {
    if (startAtEnabled) startAtInputRef.current?.focus()
  }, [startAtEnabled])

  useEffect(() => {
    const videoParams = isVideoWorld
      ? [
          ...(startAtEnabled && mmssToSeconds(startAtValue) > 0
            ? [{ name: 't', value: String(mmssToSeconds(startAtValue)) }]
            : []),
          { name: 'autoplay', value: autoplayEnabled ? '1' : '0' },
          ...(hideControlsEnabled ? [{ name: 'controls', value: '0' }] : []),
        ]
      : []

    setTextAreaInputValue(
      createEmbedCode(info, [
        ...getEmbedQueryParams({
          controlBarEnabled: includeControlBar,
          customLoadingEnabled,
          is3DCommerce,
        }),
        ...videoParams,
      ]),
    )
  }, [
    includeControlBar,
    customLoadingEnabled,
    info,
    is3DCommerce,
    isVideoWorld,
    startAtEnabled,
    startAtValue,
    autoplayEnabled,
    hideControlsEnabled,
  ])

  const handleEmbedCodeCopy = async () => {
    try {
      await navigator.clipboard.writeText(textAreaInputValue)
      showToast({
        message: t('embed-copied-toast.title'),
        type: 'success',
      })
      onCloseAction()
    } catch {
      // Inside a third-party iframe without `allow="clipboard-write"` the write is always rejected.
      // Selecting the code lets the viewer copy it by hand.
      embedCodeTextareaRef.current?.select()
      showToast({
        message: t('share-panel.copy-failed'),
        type: 'error',
      })
    }
  }

  const handleCustomLoadingToggle = () => {
    setCustomLoadingEnabled((prev) => !prev)
  }

  const handleTextInputChange = () => {
    const videoParams = isVideoWorld
      ? [
          ...(startAtEnabled && mmssToSeconds(startAtValue) > 0
            ? [{ name: 't', value: String(mmssToSeconds(startAtValue)) }]
            : []),
          { name: 'autoplay', value: autoplayEnabled ? '1' : '0' },
          ...(hideControlsEnabled ? [{ name: 'controls', value: '0' }] : []),
        ]
      : []

    const newEmbedCode = createEmbedCode(info, [
      ...getEmbedQueryParams({ customLoadingEnabled }),
      ...videoParams,
      { name: 'dl', value: textInputRef.current?.value },
      { name: 'd', value: textInputRef.current?.value },
      { name: 'mp', value: textInputRef.current?.value },

      { name: 'ml', value: textInputRef.current?.value },
    ])

    setTextAreaInputValue(newEmbedCode)
  }

  if (useControlBarEmbedDialog) {
    return (
      <div className="@max-embed-lg/embed:gap-2 flex h-full w-full flex-col items-center gap-8">
        <div className="flex w-full flex-col items-center gap-4">
          <h2 className="w-full text-center text-base leading-6 font-semibold tracking-[0.2px] text-white">
            {t('embed-modal.title')}
          </h2>
          <div className="@max-embed-md/embed:gap-2 flex w-full flex-col items-center gap-4">
            <textarea
              ref={embedCodeTextareaRef}
              value={textAreaInputValue}
              spellCheck={false}
              className="@max-embed-lg/embed:h-[87px] @max-embed-md/embed:h-12 @max-embed-md/embed:py-1 @max-embed-md/embed:text-sm bg-vive-primary-70/50 text-vive-primary-30 h-24 w-full resize-none rounded-[3px] border-none px-5 py-3 text-base leading-6 font-normal tracking-[0.2px] outline-none"
              onChange={(event) => setTextAreaInputValue(event.target.value)}
            />
            <ViverseCheckbox
              id="embed-control-bar-checkbox"
              checked={includeControlBar}
              onCheckedChange={() => setIncludeControlBar((prev) => !prev)}
              containerClassName="self-start @max-embed-lg/embed:py-0"
            >
              <span className="text-vive-primary-20 text-base leading-6 tracking-[0.2px]">
                {t('embed-modal.include-control-bar')}
              </span>
            </ViverseCheckbox>
          </div>
        </div>
        <ViverseButton
          variant="custom"
          size="custom"
          className="@max-embed-md/embed:h-7 flex h-12 min-w-[93px] shrink-0 items-center justify-center gap-2 rounded-full bg-white/15 px-4 py-2 whitespace-nowrap backdrop-blur-[50px] hover:bg-white/20 active:bg-white/15"
          onClick={handleEmbedCodeCopy}
        >
          <IconCopy className="size-5 shrink-0 fill-white" aria-hidden="true" />
          <span className="text-sm leading-[22px] font-semibold tracking-[0.15px]">
            {t('embed-modal.button')}
          </span>
        </ViverseButton>
      </div>
    )
  }

  return (
    <>
      <div className="flex items-center">
        <p className="subheader-1 text-vive-white w-full text-center">{t('embed-modal.title')}</p>
      </div>

      <div className="flex flex-col gap-3 py-2">
        <div className="flex flex-col gap-2">
          <ViverseTextarea
            className="bg-vive-primary-50/60 max-h-50 w-full border-transparent text-sm"
            showMaxLength={false}
            value={textAreaInputValue}
            onChange={(e) => setTextAreaInputValue(e.target.value)}
          />
          {!(isStandalone || isVideoWorld) && (
            <>
              <ViverseCheckbox
                id="embed-checkbox"
                checked={customLoadingEnabled}
                onCheckedChange={handleCustomLoadingToggle}
                containerClassName="py-1.5"
              >
                <span className="caption text-vive-primary-20">
                  {t('embed-modal.checkbox-label')}
                </span>
              </ViverseCheckbox>
              {customLoadingEnabled && (
                <ViverseInput
                  ref={textInputRef}
                  className="bg-vive-primary-50/60 border-transparent text-sm"
                  onChange={handleTextInputChange}
                  placeholder={t('embed-modal.input-placeholder')}
                />
              )}
            </>
          )}
          {isVideoWorld && (
            <>
              <div className="my-1 h-[1.5px] w-full bg-[#F1F7FE]/20" />
              <div className="flex flex-col">
                <div className="flex items-center justify-between">
                  <ViverseCheckbox
                    id="embed-start-at"
                    checked={startAtEnabled}
                    onCheckedChange={(v) => setStartAtEnabled(!!v)}
                    containerClassName="py-1.5"
                  >
                    <span className="caption text-vive-primary-20">
                      {t('share-modal.start-time-checkbox-label')}
                    </span>
                  </ViverseCheckbox>
                  <div className="w-20 shrink-0">
                    <ViverseInput
                      ref={startAtInputRef}
                      value={startAtValue}
                      disabled={!startAtEnabled}
                      onChange={(e) =>
                        setStartAtValue(e.target.value.replace(/[^\d:]/g, '').slice(0, 5))
                      }
                      onBlur={() => setStartAtValue(normalizeTimeInput(startAtValue))}
                      placeholder="00:00"
                      className="h-7 min-h-0 text-center text-sm"
                      containerClassName={cn(
                        'border-transparent',
                        !startAtEnabled &&
                          'focus-within:border-transparent hover:border-transparent',
                      )}
                    />
                  </div>
                </div>
                <ViverseCheckbox
                  id="embed-autoplay"
                  checked={autoplayEnabled}
                  onCheckedChange={(v) => setAutoplayEnabled(!!v)}
                  containerClassName="py-1.5"
                >
                  <span className="caption text-vive-primary-20">
                    {t('share-modal.auto-play-checkbox-label')}
                  </span>
                </ViverseCheckbox>
                <ViverseCheckbox
                  id="embed-hide-controls"
                  checked={hideControlsEnabled}
                  onCheckedChange={(v) => setHideControlsEnabled(!!v)}
                  containerClassName="py-1.5"
                >
                  <span className="caption text-vive-primary-20">
                    {t('share-modal.hide-controls-checkbox-label')}
                  </span>
                </ViverseCheckbox>
              </div>
            </>
          )}
        </div>

        <ViverseButton
          size="custom"
          className="mx-auto h-8.5 shrink-0 rounded-full px-4"
          onClick={handleEmbedCodeCopy}
        >
          <IconCopy className="mr-1.5 size-4 fill-white" />
          <span className="text-sm">{t('embed-modal.button')}</span>
        </ViverseButton>
      </div>
    </>
  )
}

export default function SharePanel({
  onCloseAction,
  shareUrl,
  title,
  info,
  currentTime,
  positionFixed,
  isVideoWorld,
  is3DCommerce,
  isStandalone,
  initialView,
  previewImageAlt,
  previewImageSrc,
  renderContentOnly = false,
  useControlBarDialog = false,
  useControlBarEmbedDialog = false,
  onSwitchToEmbedAction,
}: SharePanelProps) {
  const t = useTranslations()
  const [view, setView] = useState<'share' | 'embed'>(initialView ?? 'share')

  const handleClose = () => {
    setView('share')
    onCloseAction()
  }

  const content = (
    <>
      {view === 'share' ? (
        <ShareView
          onCloseAction={handleClose}
          shareUrl={shareUrl}
          title={title}
          currentTime={currentTime}
          isVideoWorld={isVideoWorld}
          isStandalone={isStandalone}
          previewImageAlt={previewImageAlt}
          previewImageSrc={previewImageSrc}
          onSwitchToEmbed={() => {
            if (onSwitchToEmbedAction) {
              onSwitchToEmbedAction()
              return
            }

            setView('embed')
          }}
          useControlBarDialog={useControlBarDialog}
        />
      ) : (
        <EmbedView
          info={info}
          onCloseAction={handleClose}
          is3DCommerce={is3DCommerce}
          isStandalone={isStandalone}
          isVideoWorld={isVideoWorld}
          currentTime={currentTime}
          useControlBarEmbedDialog={useControlBarEmbedDialog}
        />
      )}
    </>
  )

  if (renderContentOnly) return content

  return (
    <div
      className={cn(
        'animate-in fade-in z-navigation inset-0 flex items-center justify-center bg-black/60 duration-200',
        positionFixed ? 'fixed' : 'absolute',
      )}
      onClick={handleClose}
    >
      <div
        className={cn(
          'text-vive-white bg-vive-surface-normal/40 relative w-[calc(100%-32px)] rounded-[7px] px-6 py-4 backdrop-blur-3xl',
          view === 'embed' ? 'max-w-100' : 'max-w-120',
        )}
        onClick={(e) => e.stopPropagation()}
      >
        <button
          type="button"
          className="absolute top-1.75 right-1.75 cursor-pointer rounded-full p-1 transition-colors hover:bg-white/10"
          onClick={handleClose}
          aria-label={t('media-preview-modal.close')}
        >
          <IconClose className="size-5" />
        </button>

        {content}
      </div>
    </div>
  )
}
